import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CustomValidators, FormGroupDefinition } from 'components';
import { compare } from 'fast-json-patch';
import { asyncScheduler, Observable, of } from 'rxjs';
import { first, map, observeOn, take } from 'rxjs/operators';
import { NotificationService } from 'src/app/core/services/notification.service';
import { CodeWrapperService } from 'src/app/core/services/service-wrappers/code-wrapper.service';
import { isDeepStrictEqual } from 'util';

@Component({
  standalone: false,
  selector: 'app-diagnosis-codes-dialog',
  templateUrl: './diagnosis-codes-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./diagnosis-codes-dialog.component.scss']
})
export class DiagnosisCodesDialogComponent implements OnInit {
  formGroup = new UntypedFormGroup({});
  formInitialized = false;
  saving = false;
  CODE_TYPE = 'DiagnosisCode';
  diagnosisCodeId;
  viewOnly = false;

  formDefinitions: FormGroupDefinition[];
  getFormDefinitions(isEdit: boolean): FormGroupDefinition[] {
    return [
      {
        hideTitle: true,
        controls: [
          {
            label: 'Code',
            name: 'code',
            isReadOnly: isEdit,
            type: 'text',
            class: 'form-span-4',
            validators: [Validators.required, CustomValidators.valueInUseValidator(this.data?.activeCodes)]
          },
          {
            label: 'Description',
            name: 'name',
            type: 'text',
            class: 'form-span-8',
            validators: Validators.required
          },
          {
            label: 'Active',
            name: 'active',
            type: 'checkbox',
            class: 'form-span-4',
            initial: true,
            validators: Validators.required
          },
          {
            label: 'Code Set',
            name: 'codeSet',
            type: 'select',
            options: ['ICD10', 'ICD9'],
            class: 'form-span-8',
            initial: 'ICD10',
            validators: Validators.required
          }
        ]
      }
    ];
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private injector: Injector,
    private notificationService: NotificationService,
    public dialogRef: MatDialogRef<DiagnosisCodesDialogComponent>,
    private service: CodeWrapperService
  ) {
    this.diagnosisCodeId = data?.diagnosisCodeId;
    this.viewOnly = data?.viewOnly;
    const isEdit = this.diagnosisCodeId && this.diagnosisCodeId !== '';
    this.formDefinitions = this.getFormDefinitions(isEdit);
  }

  ngOnInit(): void {
    this.setupForm();

    if (this.diagnosisCodeId && this.diagnosisCodeId !== '') {
      this.service
        .apiV1CodeCodetypeIdGet(this.CODE_TYPE, this.diagnosisCodeId)
        .pipe(first())
        .subscribe((result) => {
          this.formGroup.patchValue(result);
          this.formGroup.get('active').patchValue(!result.inactive);
        });
    }
  }

  setupForm() {
    this.formDefinitions.forEach((sc) => {
      sc.controls.forEach((control) => {
        if (control.type !== 'empty' && control.type !== 'label') {
          this.formGroup.addControl(control.name, new UntypedFormControl(control.initial ?? '', control.validators));
        }
      });
    });
    this.formInitialized = true;
  }

  save() {
    if (this.formGroup.valid && this.saving === false) {
      this.saving = true;

      const formData = {
        code: this.formGroup.get('code').value,
        name: this.formGroup.get('name').value,
        inactive: this.formGroup.get('active').value === true ? false : true,
        codeSet: this.formGroup.get('codeSet').value
      };

      let saveObservable: Observable<any>;
      let notification;
      if (this.diagnosisCodeId) {
        notification = 'Diagnosis Code Updated';
        saveObservable = this.service
          .apiV1CodeCodetypePatchIdPatch(this.CODE_TYPE, this.diagnosisCodeId, compare({}, formData))
          .pipe(map((x: any) => x));
      } else {
        notification = 'Diagnosis Code Added';
        saveObservable = this.service.apiV1CodeCodetypeAddPost(this.CODE_TYPE, formData).pipe(map((x: any) => x));
      }

      saveObservable
        .pipe(take(1), observeOn(asyncScheduler))
        .subscribe(
          (response) => {
            this.dialogRef.close(true);
            this.notificationService.success(notification);
          },
          (err) => {
            this.notificationService.error('Saving Diagnosis Code');
            this.saving = false;
          }
        );
    }
  }
}
